/**
 * CPV-G25 S10 · the CPV parameter master's door to the server (`/bp/cpv/master/…`, datasheet §12.4).
 *
 * Conventions (services/cpv/api): every answer is the envelope `{status, data}` (a refusal `{status: "failed", code,
 * message[, field]}`); a list is one page `{results, count, page, page_size, pages}` sized by the server's
 * `cpv.api.page_size`; every POST carries an `Idempotency-Key`; a lifecycle act carries `If-Match` with the version's
 * `state_version` as read. The client decides nothing: statuses, allowed acts, validation and refusals are the server's.
 *
 * Idempotency: one key per action — the same action with the same content (the e-signature excluded, so a retry
 * after a refused password keeps its key) re-sends the key it was first sent with until the server accepts it; a
 * success forgets it. So a retry after a lost answer is replayed by the server, never done twice.
 */
import Axios from 'axios';
import { dropSharedCharts } from './cpvApi';

const BP = '/bp';
const MASTER = '/cpv/master';
const token = () => localStorage.getItem('Token') || '';
const unwrap = (r) => r.data?.data ?? r.data;

/** A random UUID v4 (crypto.randomUUID where offered; else from getRandomValues). */
export function uuid() {
  const c = globalThis.crypto; // eslint-disable-line no-undef -- an ES2020 global the lint env predates
  if (c?.randomUUID) return c.randomUUID();
  const b = c.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

const pending = new Map();   // action fingerprint → the Idempotency-Key it was first sent with
const fingerprint = (path, body) => {
  const { esign: _e, ...rest } = body || {};
  return `${path} ${JSON.stringify(rest)}`;
};
/** The key for this action: the one already in flight for the same action and content, else a new one. */
export function idempotencyKeyFor(path, body) {
  const f = fingerprint(path, body);
  if (!pending.has(f)) pending.set(f, uuid());
  return pending.get(f);
}

const headers = (extra = {}) => ({ Authorization: `Bearer ${token()}`, ...extra });
export const get = (path, params) => Axios.get(`${BP}${path}`, { params, headers: headers() }).then(unwrap);
const patch = (path, body) => { dropSharedCharts(); return Axios.patch(`${BP}${path}`, body, { headers: headers() }).then(unwrap); };
/** POST with its Idempotency-Key (and If-Match when given); the key is forgotten once the server accepts. */
export async function post(path, body, { ifMatch } = {}) {
  const f = fingerprint(path, body);
  const key = idempotencyKeyFor(path, body);
  const extra = { 'Idempotency-Key': key };
  if (ifMatch !== undefined && ifMatch !== null) extra['If-Match'] = `"${ifMatch}"`;
  dropSharedCharts();               // Q8p: a write may change what the shared plan charts show (cpvApi)
  const r = await Axios.post(`${BP}${path}`, body, { headers: headers(extra) });
  pending.delete(f);
  return unwrap(r);
}

/** The refusal's `field` (when the server names one) → its message, for inline display under that input. */
export function fieldErrorOf(refusal) {
  const field = refusal?.extra?.field;
  return field ? { [field]: refusal.message } : {};
}

/** The route key of the plant's master (`/cpv/master/plant`, ND-15: a PLANT master names no product). */
export const PLANT_KEY = 'plant';
/** The master scope word of the plant master (MT-4), which the picker names (`?scope=PLANT`; `plant` is the location). */
export const PLANT_SCOPE = 'PLANT';
/** A master's server path: a product's, or the plant's master. */
const familyPath = (key) => (key === PLANT_KEY ? `${MASTER}/plant` : `${MASTER}/products/${key}`);
/** The lifecycle object type of a master version (services/lifecycle/subjects.py). */
export const MASTER_OBJECT_TYPE = 'CPV_MASTER';

export const masterApi = {
  /** The plant's products with their master state: a page `{results: [{product_id, code, name, effective_version,
   *  open_version, open_status}], …}` plus `counts` {products, effective, draft, in_review, no_master}. */
  products: (params) => get(`${MASTER}/products/`, params),
  /** One version (`?version=<number>`; default the open one, else EFFECTIVE): the version's fields, `state_version`,
   *  `stages`, `parameters`, `mapping_gaps`, `plans_on_earlier_versions` — or `{product, master: null}` when none. */
  detail: (key, params) => get(`${familyPath(key)}/`, params),
  /** One version by its id (`GET master/versions/<id>/`): the same fields as `detail`, with `product_id` (null for the plant's). */
  version: (versionId) => get(`${MASTER}/versions/${versionId}/`),
  /** The version history, newest first (a page of version rows). */
  versions: (key, params) => get(`${familyPath(key)}/versions/`, params),
  /** The next DRAFT: v1 empty, else a copy of the EFFECTIVE version (MD-4). */
  /** `plant`: the plant the master is kept at (CPV-MT-01 F1: the row's plant when several plants are in view). */
  newVersion: (key, { reason, change_control_ref = '', rationale = '', plant = null }) => post(`${familyPath(key)}/versions/`,
    { reason, change_control_ref, rationale, ...(plant != null ? { plant } : {}) }),
  /** A draft's change-control reference and rationale. */
  editVersion: (versionId, fields) => patch(`${MASTER}/versions/${versionId}/`, fields),
  addStage: (versionId, stage) => post(`${MASTER}/versions/${versionId}/stages/`, stage),
  editStage: (versionId, stageVersionId, fields) => patch(`${MASTER}/versions/${versionId}/stages/${stageVersionId}/`, fields),
  addParameter: (versionId, fields) => post(`${MASTER}/versions/${versionId}/parameters/`, fields),
  /** Edit a draft parameter; `mapping` SAME / CHANGED (+ `mapping_reason`) maps a carried one. */
  editParameter: (versionId, parameterVersionId, fields) => patch(`${MASTER}/versions/${versionId}/parameters/${parameterVersionId}/`, fields),
  /** A parameter leaves the version: mapped DROPPED with its reason. */
  deactivateParameter: (versionId, parameterVersionId, reason) => post(`${MASTER}/versions/${versionId}/parameters/${parameterVersionId}/deactivate/`, { reason }),
  /**
   * A lifecycle act (CPV_MASTER): one of the lifecycle read's `viewer.actions`, sent as
   * `{action, stage_key, step_key, return_to?, remarks?, esign?, meanings_shown?}` with If-Match = the state_version read.
   */
  act: (versionId, stateVersion, { action, stage_key, step_key, return_to, remarks = '', esign, meanings_shown }) => post(
    `${MASTER}/versions/${versionId}/act/`,
    { action, stage_key, step_key, ...(return_to ? { return_to } : {}), remarks, ...(esign ? { esign } : {}), ...(meanings_shown ? { meanings_shown } : {}) },
    { ifMatch: stateVersion },
  ),
  /** The EFFECTIVE master's live parameters for the plan editor: `{master, parameters}`. */
  picker: (key, plant = null) => get(`${MASTER}/picker/`, { ...(key === PLANT_KEY ? { scope: PLANT_SCOPE } : { product: key }), ...(plant != null ? { plant } : {}) }),
  /** The version's lifecycle (LIFECYCLE-01): stages, history and what this person may do now (`viewer.actions`). */
  lifecycle: (versionId) => get(`/lifecycle/${MASTER_OBJECT_TYPE}/${versionId}/`),
};
